import { z } from "zod";
import type { FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// FHSS — First Home Super Saver scheme. Two pure calculation surfaces:
//
//   1. eligibilityReleasable — the four one-shot eligibility gates (≥18 at
//      determination, first-home, never-owned-AU-property, never-previously-
//      released) plus the max RELEASABLE amount, net of the $15k/yr counting
//      cap, the $50k lifetime cap, and the 85%-concessional / 100%-non-
//      concessional split.
//
//   2. benefitVsBank — the tax benefit of saving a voluntary CONCESSIONAL
//      contribution via FHSS (15% contributions tax in, then marginal-rate −
//      30% offset out) vs the same gross dollars saved in a bank account taxed
//      at the marginal rate, with the deemed-earnings (SIC) + low-income-erosion
//      honesty caveats surfaced as facts, never as a recommendation.
//
// HARD RULES (panel ship-conditions, encoded — not just commented):
//  - The scheme endpoint is an ATO determination request — a GOVERNMENT process,
//    not a financial-product purchase. So there is no inherent product handoff,
//    and $0 affiliate by design.
//  - C2 AFSL kill-line: NO super-fund / mortgage / credit handoff may reach a
//    personalised result. There is no `handoff` field by construction, and
//    `assertNoFhssHandoff` runs over every output (tested).
//  - Show the releasable figure NET of the 85% haircut + SG exclusion; surface
//    the gates and the 12-month clock as facts. Overstating the entitlement is
//    the ACL misleading-conduct exposure.
//  - Deemed associated earnings (SIC rate) is a flagged NOTE, not a computed
//    output — keeps the ruleset pure (no per-quarter SIC table baked into calc).
//
// All figures are transcribed from the verification ledgers
// (docs/research/discovery/verification/fhss-*-figures.md). Pure: inputs ->
// rules -> outputs; the assessment FY is always a parameter, never the clock.
// ---------------------------------------------------------------------------

/**
 * FHSS-specific RuleSet metadata. FHSS is a scheme, not one of the AU0x
 * per-product codes, so it carries an `engine: "fhss"` tag (like income-tax's
 * `engine` tag) plus the standard provenance fields, rather than a `product`.
 */
export interface FhssRuleSet {
  /** Engine tag — distinguishes the scheme ruleset from the AU0x products. */
  engine: "fhss";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;

  /** Max eligible voluntary contributions COUNTED per financial year ($15,000).
   * Note: the per-year/lifetime counting test counts voluntary contributions in
   * FULL; the 85%/100% split is applied separately to derive the releasable
   * amount. */
  annualEligibleCap: number;
  /** Max eligible voluntary contributions counted across ALL years ($50,000). */
  lifetimeEligibleCap: number;
  /** Proportion of COUNTED concessional contributions that is releasable (0.85
   * — they were taxed at 15% going into the fund). */
  concessionalReleaseRate: number;
  /** Proportion of COUNTED non-concessional contributions that is releasable
   * (1.0 — already taxed at the marginal rate, not in the fund). */
  nonConcessionalReleaseRate: number;
  /** Contributions-tax rate on concessional contributions going into the fund
   * (0.15; 0.30 above the Div 293 $250k income threshold — out of cohort). */
  concessionalContributionsTaxRate: number;
  /** The release tax offset on the assessable (concessional + earnings) slice
   * coming out: taxed at the marginal rate (incl. Medicare levy) LESS this
   * offset (0.30). */
  releaseTaxOffset: number;
  /** Default withholding rate the ATO applies when it can't estimate your
   * marginal rate (0.17). A note/fact, not used in the benefit math. */
  defaultWithholdingRate: number;
  /** Minimum age (at the determination request) to be eligible (18). */
  minAge: number;
  /** Contract-signing window after a release request, in months (12). */
  contractWindowMonths: number;
  /** Maximum window with an ATO extension, in months (24). */
  contractWindowMaxMonths: number;
}

// ---------------------------------------------------------------------------
// Surface 1 — eligibilityReleasable
// ---------------------------------------------------------------------------

/**
 * Prior FHSS-eligible voluntary contributions, per the $15k/$50k COUNTING test.
 * Both concessional and non-concessional voluntary contributions count in FULL
 * toward the caps (the 85%/100% split is a separate step for the releasable
 * amount). Mandated employer Super Guarantee (SG) is NOT eligible and must not
 * be included by the caller — these fields are voluntary contributions only.
 */
export const FhssContributionsSchema = z.object({
  /** Voluntary CONCESSIONAL contributions made THIS FY (salary sacrifice /
   * personal-deductible). Excludes employer SG. */
  concessionalThisYear: z.number().min(0).default(0),
  /** Voluntary NON-CONCESSIONAL (after-tax) contributions made THIS FY. */
  nonConcessionalThisYear: z.number().min(0).default(0),
  /** FHSS-eligible voluntary contributions already COUNTED in PRIOR financial
   * years (toward the $50k lifetime cap). Whole, pre-split dollars. */
  priorYearsCounted: z.number().min(0).default(0),
});
export type FhssContributions = z.infer<typeof FhssContributionsSchema>;

/**
 * The four eligibility gates. Each is a question of FACT the caller asserts —
 * the tool does NOT determine eligibility (e.g. it never decides residency or
 * tests the hardship-ownership exception); it reflects the user's answers and
 * states whether they meet the published gates. Framed as information.
 */
export const FhssEligibilityInputSchema = z.object({
  /** Age at the (intended) FHSS determination request. Gate: ≥ 18. */
  ageAtDetermination: z.number().int().min(0),
  /** True if buying/building a first home to live in (the first-home gate). */
  firstHome: z.boolean(),
  /**
   * Has EVER owned property in Australia (incl. investment / commercial /
   * vacant land / a lease of land). The gate requires NEVER having owned —
   * unless the financial-hardship exception applies (asserted separately). The
   * tool does not adjudicate hardship; it surfaces the exception as a note.
   */
  hasOwnedAuPropertyBefore: z.boolean(),
  /** Has the financial-hardship exception been determined to apply? When true,
   * prior ownership does not fail the gate. Caller-asserted; not adjudicated. */
  financialHardshipException: z.boolean().default(false),
  /** Has the user EVER made a valid FHSS release request before? Gate: must be
   * false (one-shot). */
  hasPreviouslyReleased: z.boolean(),
  /** Prior + this-year voluntary contributions for the releasable math. */
  contributions: FhssContributionsSchema,
  /** The financial year being assessed, e.g. "2025-26". Passed in. */
  assessmentYear: z.string(),
});
export type FhssEligibilityInput = z.input<typeof FhssEligibilityInputSchema>;

/** Per-gate pass/fail with the reason, so the UI can surface the specific gate
 * that failed rather than a bare boolean. */
export interface FhssGateResult {
  /** Stable id of the gate. */
  gate: "age" | "firstHome" | "neverOwned" | "neverReleased";
  /** Whether this gate passes on the supplied facts. */
  pass: boolean;
}

export interface FhssEligibilityOutput {
  /** Whether ALL four gates pass on the supplied facts. */
  eligible: boolean;
  /** Per-gate breakdown (which specific gate(s) failed). */
  gates: readonly FhssGateResult[];
  /** Total voluntary contributions COUNTED this FY (concessional + non-
   * concessional, in full), CAPPED at the $15,000 annual cap. */
  countedThisYear: number;
  /** Dollars of this-year voluntary contributions ABOVE the $15k annual cap
   * (do not count toward FHSS). 0 if at/under the cap. */
  overAnnualCap: number;
  /** Total counted across all years (prior + this year), CAPPED at $50,000. */
  countedLifetime: number;
  /** Remaining lifetime headroom under the $50k cap (0 once exhausted). */
  lifetimeHeadroom: number;
  /**
   * Maximum RELEASABLE contributions, NET of the 85%/100% split and both caps.
   * Excludes ATO-deemed associated earnings, which are added on release at the
   * SIC rate (a flagged note, not computed here).
   */
  maxReleasableContributions: number;
  /** Whether deemed associated earnings (SIC) will be ADDED on top of the
   * releasable contributions at release. Always true when there is anything to
   * release — surfaced as a fact so the figure isn't read as the final total. */
  earningsAddedOnRelease: boolean;
}

// ---------------------------------------------------------------------------
// Surface 2 — benefitVsBank
// ---------------------------------------------------------------------------

/**
 * Inputs for the FHSS-vs-bank tax-benefit comparison. We compare saving a
 * voluntary CONCESSIONAL (salary-sacrifice / personal-deductible) contribution
 * through FHSS against saving the same GROSS pre-tax dollars in a bank account.
 * The concessional path is where the tax benefit lives (15% in, marginal−30%
 * out); a non-concessional contribution is after-tax dollars in and out, so its
 * tax benefit is ~nil and it is out of scope for this comparison surface.
 */
export const FhssBenefitInputSchema = z.object({
  /** Gross (pre-tax) amount to save this year, e.g. $10,000. Capped at the
   * $15k annual eligible cap for the FHSS leg. */
  grossContribution: z.number().min(0),
  /** Taxable income for the year — selects the marginal rate via the shared
   * income-tax engine. */
  taxableIncome: z.number().min(0),
  /** The financial year being assessed, e.g. "2025-26". Passed in. */
  assessmentYear: z.string(),
});
export type FhssBenefitInput = z.input<typeof FhssBenefitInputSchema>;

export interface FhssBenefitOutput {
  /** Gross dollars compared (after the $15k annual-cap clamp on the FHSS leg). */
  grossContribution: number;
  /** Marginal income-tax rate applied (bracket rate, from the income-tax
   * engine). */
  marginalRate: number;
  /** Medicare levy rate added to the marginal slice (the release tax is
   * Medicare-inclusive). */
  medicareRate: number;
  /** Effective marginal rate used both for the bank-account tax and as the base
   * of the release tax = marginalRate + medicareRate. */
  effectiveMarginalRate: number;
  /** Net release-tax rate on the FHSS concessional slice coming out =
   * max(0, effectiveMarginalRate − releaseTaxOffset). Can be 0 for low earners. */
  releaseTaxRate: number;

  /** FHSS leg: net amount in the fund AFTER the 15% contributions tax going in
   * (gross × (1 − 15%)). */
  fhssAfterContributionsTax: number;
  /** FHSS leg: tax paid on release of that slice (fhssAfterContributionsTax ×
   * releaseTaxRate). */
  fhssReleaseTax: number;
  /** FHSS leg: amount in hand after both taxes (contributions tax + release
   * tax). Excludes deemed earnings — see `earningsCaveat`. */
  fhssNet: number;

  /** Bank leg: the gross saved into a bank account is taxed at the marginal
   * rate first (you save POST-tax dollars), so net = gross × (1 −
   * effectiveMarginalRate). */
  bankNet: number;

  /**
   * The contributions-tax benefit: how much MORE of the gross survives the
   * "money in" + "money out" tax under FHSS vs the bank, on the contributions
   * alone. fhssNet − bankNet. Stated as a FACT (a tax difference), never as a
   * "you should" or a guaranteed "save $X" — copy frames it accordingly.
   */
  contributionsTaxBenefit: number;

  /** Honesty caveat: FHSS adds ATO-DEEMED earnings (SIC rate), not your fund's
   * real return — set so copy can surface it. Not computed here (keeps the rule
   * pure: no per-quarter SIC table in calc). */
  earningsCaveat: true;
  /**
   * Honesty caveat: for a LOW-income earner whose marginal rate is at/below the
   * 15% contributions-tax rate, the FHSS tax wedge erodes or reverses — the 15%
   * tax going in can exceed the tax they'd have paid in a bank account. True
   * when effectiveMarginalRate ≤ concessionalContributionsTaxRate.
   */
  lowIncomeErosion: boolean;
}

// ---------------------------------------------------------------------------
// Hard-rule guard — no super-fund / mortgage / credit handoff on a result
// ---------------------------------------------------------------------------

/**
 * C2 AFSL kill-line guard (panel lens 2 made binding). FHSS sits one tile away
 * from a CRITICAL breach: attaching a SUPER-FUND handoff (an interest in super
 * is a s764A financial product) or a MORTGAGE / credit-broker handoff (regulated
 * credit) to the personalised releasable figure or the benefit comparison is the
 * AFSL kill-line. There is no `handoff` field on any FHSS output by
 * construction; this guard additionally runs over every serialised output and
 * throws if any super-fund / mortgage / credit / advice identifier leaks in.
 * Tested in calculate.test.ts.
 */
export const FORBIDDEN_FHSS_HANDOFF_TERMS = [
  "super fund",
  "super-fund",
  "switch fund",
  "switch your fund",
  "best fund",
  "compare funds",
  "mortgage",
  "home loan",
  "home-loan",
  "broker",
  "refinance",
  "pre-approval",
  "preapproval",
  "credit card",
  "personal loan",
  "you should",
  "we recommend",
  "save $",
] as const;

export function assertNoFhssHandoff(output: unknown): void {
  const serialised = JSON.stringify(output).toLowerCase();
  for (const term of FORBIDDEN_FHSS_HANDOFF_TERMS) {
    if (serialised.includes(term)) {
      throw new Error(
        `FHSS output leaked a forbidden super-fund/mortgage/credit/advice term: "${term}"`,
      );
    }
  }
}
